import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { format } from 'date-fns';

const getOfficeHeading = (name) => {
  if (!name) return null;
  const trimmed = String(name).trim();
  if (!trimmed) return null;
  if (/^office of\b/i.test(trimmed)) return trimmed;
  return `Office of ${trimmed}`;
};

const DispatchSlip = React.forwardRef(({ dispatch, user, office }, ref) => {
  if (!dispatch) return null;

  const officeName = office?.name || user?.officeName;
  const officeCode = office?.code || user?.officeCode;
  const officeAddress = office?.address;

  return (
    <div ref={ref} className="p-8 bg-white max-w-2xl mx-auto border border-slate-200 shadow-sm print:shadow-none print:border-none print:w-full print:max-w-none">
      <div className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-900 via-sky-900 to-indigo-900 text-white">
        <div className="relative p-6">
          <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-amber-400/20 to-transparent" />
          <div className="relative flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-white/40 bg-white/10 text-[10px] font-black tracking-[0.24em] text-white/90">
              GOP
            </div>
            <div className="flex-1 text-center">
              <h1 className="text-2xl font-black uppercase tracking-[0.18em] text-white">WATER AND POWER DEVELOPMENT</h1>
              <h2 className="mt-1 text-lg font-semibold tracking-wide text-sky-100">
                {getOfficeHeading(officeName) || 'Office'}
              </h2>
              <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-200">Official Dispatch Record</p>
              {(officeCode || officeAddress) ? (
                <p className="mt-2 text-[11px] text-sky-100/90">
                  {officeCode ? `${officeCode}` : ''}
                  {officeCode && officeAddress ? ' · ' : ''}
                  {officeAddress ? officeAddress : ''}
                </p>
              ) : null}
            </div>
            <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-white/30 bg-white/10 text-[10px] font-bold tracking-[0.18em] text-sky-100">
              WAPDA
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-start mb-8">
        <div>
          <p className="text-sm text-gray-500 font-medium uppercase tracking-wider">Dispatch Number</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{dispatch.dispatchNumber}</p>
          {dispatch.isConfidential && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mt-2">
              CONFIDENTIAL
            </span>
          )}
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500 font-medium uppercase tracking-wider">Date</p>
          <p className="text-xl font-medium text-gray-900 mt-1">{format(new Date(dispatch.dateIssued), 'dd MMM yyyy')}</p>
          <p className="text-sm text-gray-500 mt-1">Mode: {dispatch.modeOfDispatch}</p>
        </div>
      </div>

      <div className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200 print:bg-white print:border-gray-300">
        <div className="mb-6">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">To</span>
          <p className="text-lg font-bold text-gray-900">{dispatch.recipient}</p>
          {dispatch.recipientAddress && (
            <p className="text-gray-700 whitespace-pre-wrap mt-1 text-sm">{dispatch.recipientAddress}</p>
          )}
        </div>
        <div>
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Subject</span>
          <p className="text-gray-900 font-medium text-lg leading-relaxed">{dispatch.subject}</p>
        </div>
      </div>

      <div className="flex justify-between items-end mt-12 pt-8 border-t border-gray-200">
        <div className="flex flex-col items-center">
          <QRCodeSVG 
            value={JSON.stringify({
              id: dispatch.id,
              number: dispatch.dispatchNumber,
              date: dispatch.dateIssued,
              recipient: dispatch.recipient
            })}
            size={100}
            level="M"
            includeMargin={true}
          />
          <span className="text-[10px] text-gray-500 mt-1 font-mono uppercase tracking-widest">Scan to Verify</span>
        </div>
        
        <div className="text-right">
          <div className="h-16 mb-2"></div> {/* Space for signature */}
          <div className="border-t border-gray-400 w-48 ml-auto pt-2">
            <p className="font-bold text-gray-900">{dispatch.sendingOfficerName || user?.name || 'Authorized Signatory'}</p>
            <p className="text-sm text-gray-500">{dispatch.sendingOfficerDesignation || user?.role || 'Designation'}</p>
          </div>
        </div>
      </div>
      
      <div className="mt-12 text-center text-[10px] text-gray-400 border-t border-gray-100 pt-4 print:fixed print:bottom-4 print:left-0 print:w-full">
        Generated on {format(new Date(), 'dd MMM yyyy HH:mm')} | DAK Management System | Official Record
      </div>
    </div>
  );
});
DispatchSlip.displayName = 'DispatchSlip';

export default DispatchSlip;
